import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { getCloudModuleStatus, getCloudModuleFiles, getCloudProjects, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FolderOpen, FileText, Folder, ExternalLink, CloudOff, Loader2, RefreshCw, Sparkles } from "lucide-react";

const MODULES = ["PUNCH", "ERP", "CRM", "COMPTA", "PAIE", "DEVIS_AI", "TAKE_OFF", "SUPPORT", "MOBILE", "CORE"];
const LABELS = { CORE: "CORE", ERP: "ERP", PUNCH: "PUNCH", CRM: "CRM", COMPTA: "COMPTA", PAIE: "PAIE", DEVIS_AI: "DEVIS AI", TAKE_OFF: "TAKE OFF", SUPPORT: "SUPPORT", MOBILE: "MOBILE" };

function bytes(n) {
  if (!n && n !== 0) return "—";
  const u = ["o", "Ko", "Mo", "Go"]; let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i ? 1 : 0)} ${u[i]}`;
}

export default function DocumentsPage() {
  const [active, setActive] = useState("PUNCH");
  const [status, setStatus] = useState(null);
  const [files, setFiles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => { getCloudProjects().then(setProjects).catch(() => {}); }, []);

  const load = async (mod, proj) => {
    setLoading(true);
    try {
      const [st, fl] = await Promise.all([
        getCloudModuleStatus(mod),
        getCloudModuleFiles(mod, proj === "all" ? undefined : proj),
      ]);
      setStatus(st);
      setFiles(fl.files || []);
    } catch (e) {
      setStatus(null); setFiles([]);
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(active, projectId); /* eslint-disable-next-line */ }, [active, projectId]);

  const notConnected = status && !status.connected;

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div>
        <h1 className="text-3xl font-extrabold text-white flex items-center gap-2.5">
          <FolderOpen className="w-7 h-7 text-[#EAB308]" /> Documents cloud
        </h1>
        <p className="mt-1 text-white/55">
          Fichiers synchronisés depuis votre cloud, organisés par module. Alimentés par PROGÈRE Cloud Connect.
        </p>
      </div>

      {/* Onglets modules */}
      <div className="flex flex-wrap gap-2" data-testid="documents-module-tabs">
        {MODULES.map((m) => (
          <button key={m} data-testid={`doc-tab-${m}`} onClick={() => setActive(m)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              active === m ? "bg-[#EAB308] text-black" : "bg-white/5 border border-white/10 text-white/60 hover:text-white"
            }`}>
            {LABELS[m] || m}
          </button>
        ))}
      </div>

      {/* Barre : projet + refresh + résumé IA */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-white/40">Projet :</span>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger data-testid="doc-project-filter" className="h-9 w-56 bg-black/50 border-white/15 text-white/80 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0a0a] border-white/15 text-white">
              <SelectItem value="all" className="text-xs">Tous les projets</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3">
          {status && (
            <span className="text-[11px] text-white/45 inline-flex items-center gap-1.5" data-testid="doc-ai-summary">
              <Sparkles className="w-3.5 h-3.5 text-[#EAB308]" />
              {status.ai_allowed_categories?.length || 0} catégorie(s) autorisée(s) IA
            </span>
          )}
          <Button data-testid="doc-refresh" onClick={() => load(active, projectId)}
            className="h-9 bg-white/10 border border-white/15 text-white text-xs gap-1.5 hover:bg-white/15">
            <RefreshCw className="w-3.5 h-3.5" /> Actualiser
          </Button>
        </div>
      </div>

      {/* Contenu */}
      <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-5" data-testid="documents-list">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#EAB308] animate-spin" /></div>
        ) : notConnected ? (
          <div className="flex flex-col items-center text-center py-14" data-testid="documents-empty-noconn">
            <CloudOff className="w-10 h-10 text-white/25 mb-3" />
            <p className="text-white/70 font-medium">Aucun cloud connecté</p>
            <p className="text-sm text-white/45 mt-1 max-w-md">
              Connectez Microsoft OneDrive dans PROGÈRE Cloud Connect, choisissez vos catégories, puis lancez une
              synchronisation pour voir apparaître vos vrais documents ici.
            </p>
            <Button data-testid="documents-go-cloud" onClick={() => (window.location.href = "/app/cloud-connect")}
              className="mt-4 h-10 bg-[#EAB308] text-black font-semibold px-5 hover:opacity-90">
              Ouvrir PROGÈRE Cloud Connect
            </Button>
          </div>
        ) : files.length === 0 ? (
          <div className="flex flex-col items-center text-center py-14" data-testid="documents-empty">
            <FileText className="w-10 h-10 text-white/25 mb-3" />
            <p className="text-white/70 font-medium">Aucun document synchronisé pour {LABELS[active] || active}</p>
            <p className="text-sm text-white/45 mt-1">Lancez une synchronisation depuis PROGÈRE Cloud Connect.</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {files.map((f) => (
              <div key={f.external_file_id} data-testid={`doc-row-${f.external_file_id}`}
                className="flex items-center gap-3 py-3">
                {f.is_folder ? <Folder className="w-4 h-4 text-[#EAB308] shrink-0" /> : <FileText className="w-4 h-4 text-white/50 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{f.file_name || "(sans nom)"}</div>
                  <div className="text-[11px] text-white/40">
                    {(f.data_type || "—")} · {bytes(f.size_bytes)} · {f.sync_status}
                  </div>
                </div>
                {f.web_url && (
                  <a href={f.web_url} target="_blank" rel="noreferrer" data-testid={`doc-open-${f.external_file_id}`}
                    className="text-white/40 hover:text-[#EAB308]"><ExternalLink className="w-4 h-4" /></a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
